import { Box, Typography } from '@mui/material';

import { isMcpPermission, MCP_PERMISSIONS, mcpActionLabel } from './mcpPermissions';

export function a11yProps(index) {
    return {
        id: `simple-tab-${index}`,
        'aria-controls': `simple-tabpanel-${index}`,
    };
}

export function CustomTabPanel(props) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`simple-tabpanel-${index}`}
            aria-labelledby={`simple-tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ p: 3 }}>
                    <Typography>{children}</Typography>
                </Box>
            )}
        </div>
    );
}

export const SCOPES = {
    "agroup": { name: "Access group", },
    "atag": { name: "Access tag", },
    "chat": { name: "Chat" },
    "chat_template": { name: "Chat template" },
    "import": { name: "Import" },
    "itinerary": { name: "Itinerary" },
    "notification": { name: "Notification" },
    "region": { name: "Region" },
    "sentiment_analysis": { name: "Sentiment analysis" },
    "tour": { name: "Tour" },
    "tour_operator": { name: "Tour operator" },
    "tour_participant": { name: "Tour participant" },
    "pre_trip_item": { name: "Pre-trip item" },
    "trip_type": { name: "Trip type" },
    "user": { name: "User" },
    "display_user_bio": { name: "", legacy: true },
    "user_can_access_admin": { name: "User can access admin UI", legacy: true },
    "view_user_positions": { name: "View user positions", legacy: true },
    "broadcast_user_position": { name: "Broadcast user position", legacy: true },
    "launch_policy": { name: "Launch policy" },
    "ai": { name: "AI Suite" },
    "web_widget": { name: "Web Chat Widget" },
    "vehicle_fleet": { name: "Vehicle fleet" },
    "api_credential": { name: "API credential" },
    "activity_category": { name: "Activity category" },
    "tour_media": { name: "Tour media" },
    // Client MCP (backend plan 2026-10-20): scopes of AI-over-MCP permissions only (./mcpPermissions.js).
    "triage": { name: "Issue triage" },
    "analytics": { name: "Analytics" },
    "vocabulary": { name: "AI vocabulary" },
    "ai_edit": { name: "AI editing" },
}

export const ACTIONS = {
    "read_only": { name: "Read-only (restrict message writing in the dashboard)" },
    "delete_messages": { name: "Delete messages" },
    "delete_own_messages": { name: "Delete own chat messages" },
    "manage_own": {name: "Manage own"},
    "metrics": { name: "View metrics (Analytics tab)" },
    "view_history": { name: "View history" },
    "read_location": { name: "Read location" },
    "share_read_receipts": { name: "Share read receipts" },
    "web_widget": { name: "Web chat widget conversations" },
    "move_to_licensee": { name: "Move chats to a licensee" },
    "disable_mobile_app_access": { name: "Disable mobile app access" },
    "should_have_personal_main_office_conversation": { name: "Should have personal main office conversation" },
    "update_details": { name: "Update details" },
    "update_email": { name: "Update email" },
    "update_phone": { name: "Update phone" },
    "update_tag_categories": { name: "Update tag categories" },
}

export const SCOPE = (key) => SCOPES[key];

export const ACTION = (key) => ACTIONS[key];

/** An action's words under its scope's heading: an AI-over-MCP permission by the backend's name ("AI over MCP —
 *  list"), else ACTIONS; null when neither knows it (the page then shows the action itself). */
export const actionName = (scope, action) => mcpActionLabel(scope, action) || ACTION(action)?.name || null;

const MCP_ORDER = Object.keys(MCP_PERMISSIONS);

/** A scope's actions in the order the page lists them: the regular ones alphabetically, then the AI-over-MCP ones in
 *  the catalogue's order (list, read, propose, approve). */
export const orderActions = (scope, actions) => {
    const regular = actions.filter((action) => !isMcpPermission(scope, action)).sort();
    const mcp = actions
        .filter((action) => isMcpPermission(scope, action))
        .sort((a, b) => MCP_ORDER.indexOf(`${scope}.${a}`) - MCP_ORDER.indexOf(`${scope}.${b}`));
    return [...regular, ...mcp];
};

/** The heading over a scope's AI-over-MCP permissions: they are explicit grants (backend `ag_has_explicit_perm`). */
export const MCP_GROUP_TITLE = 'AI over MCP — explicit grants (Full access does not include them)';

export { isMcpPermission };

export const capitalize = (s) => (s.charAt(0).toUpperCase() + s.slice(1));

// Shared by the create dialog (AccessGroupsList) and the edit page (AccessGroupEdit) so the
// two leave prompts cannot drift apart. Wording matches the rest of the dashboard's edit
// forms; the old "AccessGroup:" prefix was a model name, and the UI says "Access group".
export const LEAVE_WITHOUT_SAVING_MESSAGE = 'Are you sure you want to leave without saving changes?';

// CP-5260: the access tag screens are dismissed with [CANCEL] rather than navigated away
// from, so Rea asked for this wording rather than the "leave without saving" one.
export const CANCEL_CHANGES_MESSAGE = 'Are you sure you want to cancel changes?';

export const ConfirmationDialogText = {
    delete_access_tag: { title: 'Please confirm', body: 'Do you really want to delete this access_tag?', okButtonText: 'Delete', cancelButtonText: 'Cancel' },
};
